import openai from '../lib/openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes player performance data and generates AI-driven talent recommendations.
 * @param {Array} players - Array of player objects with performance data.
 * @returns {Promise<Object>} AI-generated talent recommendations.
 */
export async function analyzeTalentRecommendations(players) {
  try {
    if (!players || players?.length === 0) {
      return { recommendations: [], error: null };
    }

    // Prepare player data summary for AI analysis
    const playerSummary = players?.slice(0, 20)?.map(p => ({
      name: p?.fullName,
      age: p?.age,
      position: p?.position,
      team: p?.currentTeam,
      goals: p?.goalsScored || 0,
      assists: p?.assists || 0,
      motm: p?.motmAwards || 0,
      appearances: p?.totalAppearances || 0,
      availableForTransfer: p?.availableForTransfer,
      marketValue: p?.marketValue,
      internationalCaps: p?.internationalCaps || 0
    }));

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert football talent scout and analyst. Analyze player performance data and provide actionable talent recommendations. Focus on identifying high-potential players, transfer opportunities, and emerging talent based on statistics and performance metrics.' 
        },
        { 
          role: 'user', 
          content: `Analyze these players and provide top 5 talent recommendations with reasoning:\n\n${JSON.stringify(playerSummary, null, 2)}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'talent_recommendations',
          schema: {
            type: 'object',
            properties: {
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    playerName: { type: 'string' },
                    position: { type: 'string' },
                    rating: { type: 'number' },
                    reasoning: { type: 'string' },
                    strengths: { type: 'array', items: { type: 'string' } },
                    recommendation: { type: 'string' }
                  },
                  required: ['playerName', 'position', 'rating', 'reasoning', 'strengths', 'recommendation']
                }
              }
            },
            required: ['recommendations'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return { recommendations: result?.recommendations || [], error: null };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in talent recommendations:', error);
    }
    return { recommendations: [], error: errorInfo?.message };
  }
}

/**
 * Generates trajectory predictions for players based on historical performance.
 * @param {Array} players - Array of player objects with performance data.
 * @returns {Promise<Object>} AI-generated trajectory predictions.
 */
export async function generateTrajectoryPredictions(players) {
  try {
    if (!players || players?.length === 0) {
      return { predictions: [], error: null };
    }

    // Focus on top performers for trajectory analysis
    const topPlayers = players
      ?.sort((a, b) => {
        const scoreA = (a?.goalsScored || 0) * 3 + (a?.assists || 0) * 2 + (a?.motmAwards || 0) * 5;
        const scoreB = (b?.goalsScored || 0) * 3 + (b?.assists || 0) * 2 + (b?.motmAwards || 0) * 5;
        return scoreB - scoreA;
      })
      ?.slice(0, 10)
      ?.map(p => ({
        name: p?.fullName,
        age: p?.age,
        position: p?.position,
        team: p?.currentTeam,
        goals: p?.goalsScored || 0,
        assists: p?.assists || 0,
        motm: p?.motmAwards || 0,
        appearances: p?.totalAppearances || 0,
        internationalCaps: p?.internationalCaps || 0,
        contractStatus: p?.contractStatus
      }));

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert football analyst specializing in career trajectory predictions. Analyze player performance data and predict their career trajectory over the next 2-3 years. Consider age, current performance, potential, and market trends.' 
        },
        { 
          role: 'user', 
          content: `Predict career trajectories for these top players:\n\n${JSON.stringify(topPlayers, null, 2)}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'trajectory_predictions',
          schema: {
            type: 'object',
            properties: {
              predictions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    playerName: { type: 'string' },
                    currentLevel: { type: 'string' },
                    predictedLevel: { type: 'string' },
                    trajectory: { type: 'string' },
                    confidence: { type: 'number' },
                    keyFactors: { type: 'array', items: { type: 'string' } },
                    timeframe: { type: 'string' }
                  },
                  required: ['playerName', 'currentLevel', 'predictedLevel', 'trajectory', 'confidence', 'keyFactors', 'timeframe']
                }
              }
            },
            required: ['predictions'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'medium'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return { predictions: result?.predictions || [], error: null };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in trajectory predictions:', error);
    }
    return { predictions: [], error: errorInfo?.message };
  }
}

/**
 * Generates scouting insights based on player, coach, and goalkeeper data.
 * @param {Object} data - Object containing players, coaches, and goalkeepers arrays.
 * @returns {Promise<Object>} AI-generated scouting insights.
 */
export async function generateScoutingInsights(data) {
  try {
    const { players = [], coaches = [], goalkeepers = [] } = data;

    if (players?.length === 0 && coaches?.length === 0 && goalkeepers?.length === 0) {
      return { insights: [], error: null };
    }

    // Prepare comprehensive data summary
    const dataSummary = {
      totalPlayers: players?.length,
      totalCoaches: coaches?.length,
      totalGoalkeepers: goalkeepers?.length,
      topPlayers: players?.slice(0, 10)?.map(p => ({
        name: p?.fullName,
        position: p?.position,
        goals: p?.goalsScored || 0,
        assists: p?.assists || 0,
        age: p?.age,
        availableForTransfer: p?.availableForTransfer
      })),
      topCoaches: coaches?.slice(0, 5)?.map(c => ({
        name: c?.fullName,
        club: c?.presentClub,
        winRate: c?.totalGamesManaged > 0 ? ((c?.gamesWon / c?.totalGamesManaged) * 100)?.toFixed(1) : 0,
        gamesManaged: c?.totalGamesManaged
      })),
      topGoalkeepers: goalkeepers?.slice(0, 5)?.map(g => ({
        name: g?.fullName,
        cleanSheets: g?.cleanSheets || 0,
        gamesPlayed: g?.gamesPlayed || 0,
        age: g?.age
      }))
    };

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a professional football scout providing strategic insights for talent acquisition. Analyze the performance database and identify key patterns, market opportunities, emerging trends, and actionable scouting recommendations.' 
        },
        { 
          role: 'user', 
          content: `Generate comprehensive scouting insights from this performance database:\n\n${JSON.stringify(dataSummary, null, 2)}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'scouting_insights',
          schema: {
            type: 'object',
            properties: {
              insights: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    category: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    priority: { type: 'string' },
                    actionItems: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['category', 'title', 'description', 'priority', 'actionItems']
                }
              }
            },
            required: ['insights'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return { insights: result?.insights || [], error: null };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in scouting insights:', error);
    }
    return { insights: [], error: errorInfo?.message };
  }
}

/**
 * Generates AI-enhanced performance insights for the dashboard.
 * @param {Object} data - Object containing players, coaches, and goalkeepers arrays.
 * @returns {Promise<Object>} AI-generated performance insights.
 */
export async function generatePerformanceInsights(data) {
  try {
    const { players = [], coaches = [], goalkeepers = [] } = data;

    if (players?.length === 0 && coaches?.length === 0 && goalkeepers?.length === 0) {
      return { insights: [], error: null };
    }

    // Calculate key metrics
    const metrics = {
      totalRecords: players?.length + coaches?.length + goalkeepers?.length,
      avgPlayerGoals: players?.length > 0 ? (players?.reduce((sum, p) => sum + (p?.goalsScored || 0), 0) / players?.length)?.toFixed(1) : 0,
      avgPlayerAssists: players?.length > 0 ? (players?.reduce((sum, p) => sum + (p?.assists || 0), 0) / players?.length)?.toFixed(1) : 0,
      transferAvailable: players?.filter(p => p?.availableForTransfer)?.length,
      youngTalent: players?.filter(p => p?.age && p?.age < 23)?.length,
      internationalPlayers: players?.filter(p => (p?.internationalCaps || 0) > 0)?.length,
      avgCoachWinRate: coaches?.length > 0 ? (coaches?.reduce((sum, c) => {
        const winRate = c?.totalGamesManaged > 0 ? (c?.gamesWon / c?.totalGamesManaged) * 100 : 0;
        return sum + winRate;
      }, 0) / coaches?.length)?.toFixed(1) : 0,
      avgGoalkeeperCleanSheets: goalkeepers?.length > 0 ? (goalkeepers?.reduce((sum, g) => sum + (g?.cleanSheets || 0), 0) / goalkeepers?.length)?.toFixed(1) : 0
    };

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are a football analytics expert. Analyze performance metrics and generate 5-6 actionable insights highlighting exceptional performers, emerging patterns, market opportunities, and strategic recommendations.' 
        },
        { 
          role: 'user', 
          content: `Generate performance insights from these metrics:\n\n${JSON.stringify(metrics, null, 2)}` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'performance_insights',
          schema: {
            type: 'object',
            properties: {
              insights: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string' },
                    icon: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    metric: { type: 'string' },
                    category: { type: 'string' }
                  },
                  required: ['type', 'icon', 'title', 'description', 'metric', 'category']
                }
              }
            },
            required: ['insights'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return { insights: result?.insights || [], error: null };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in performance insights:', error);
    }
    return { insights: [], error: errorInfo?.message };
  }
}